import { CircleDot, Columns3, Eraser, Layers3, Waves, X } from 'lucide-react';
import { modeMeta } from '../config';
import type { InteractionMode, StudioConfig } from '../types';
import { InspectorSection, RangeField, ToggleField } from './Controls';

interface RightInspectorProps {
  config: StudioConfig;
  onUpdate: (patch: Partial<StudioConfig>) => void;
  onCloseMobile: () => void;
}

const icons = {
  spotlight: CircleDot,
  split: Columns3,
  wave: Waves,
  erase: Eraser,
  parallax: Layers3,
};

export function RightInspector({ config, onUpdate, onCloseMobile }: RightInspectorProps) {
  const setMode = (mode: InteractionMode) => onUpdate({ mode });
  const active = modeMeta.find((mode) => mode.id === config.mode)!;

  return (
    <aside className="right-inspector inspector-panel" aria-label="交互效果面板">
      <div className="inspector-titlebar">
        <div><span>Effects</span><strong>交互引擎</strong></div>
        <button className="mobile-close" type="button" onClick={onCloseMobile} aria-label="关闭面板"><X size={17} /></button>
      </div>
      <div className="inspector-scroll">
        <section className="mode-list" aria-label="选择交互效果">
          {modeMeta.map((mode) => {
            const Icon = icons[mode.id];
            const selected = config.mode === mode.id;
            return (
              <button
                key={mode.id}
                type="button"
                className={selected ? 'selected' : ''}
                aria-pressed={selected}
                onClick={() => setMode(mode.id)}
              >
                <Icon size={18} />
                <span><strong>{mode.label}</strong><small>{mode.metaphor}</small></span>
                <em>{mode.short}</em>
              </button>
            );
          })}
        </section>

        <InspectorSection title={`${active.label}参数`}>
          {config.mode === 'spotlight' && (
            <>
              <RangeField label="聚光半径" value={config.radius} min={80} max={520} unit="px" onChange={(radius) => onUpdate({ radius })} />
              <RangeField label="柔边宽度" value={config.feather} min={0} max={180} unit="px" onChange={(feather) => onUpdate({ feather })} />
              <RangeField label="跟随速度" value={config.follow} min={2} max={30} onChange={(follow) => onUpdate({ follow })} />
            </>
          )}
          {config.mode === 'split' && (
            <>
              <RangeField label="分界柔边" value={config.splitFeather} min={0} max={80} unit="px" onChange={(splitFeather) => onUpdate({ splitFeather })} />
              <RangeField label="跟随速度" value={config.follow} min={2} max={30} onChange={(follow) => onUpdate({ follow })} />
            </>
          )}
          {config.mode === 'wave' && (
            <>
              <RangeField label="波浪振幅" value={config.waveAmplitude} min={8} max={90} unit="px" onChange={(waveAmplitude) => onUpdate({ waveAmplitude })} />
              <RangeField label="波浪频率" value={config.waveFrequency} min={1} max={7} onChange={(waveFrequency) => onUpdate({ waveFrequency })} />
              <RangeField label="流动速度" value={config.waveSpeed} min={0} max={100} unit="%" onChange={(waveSpeed) => onUpdate({ waveSpeed })} />
              <RangeField label="推进跟随" value={config.follow} min={2} max={30} onChange={(follow) => onUpdate({ follow })} />
            </>
          )}
          {config.mode === 'erase' && (
            <>
              <RangeField label="画笔大小" value={config.brushSize} min={40} max={360} unit="px" onChange={(brushSize) => onUpdate({ brushSize })} />
              <RangeField label="画笔柔边" value={config.brushFeather} min={0} max={100} unit="%" onChange={(brushFeather) => onUpdate({ brushFeather })} />
              <ToggleField label="保留轨迹" description="关闭后轨迹会自动淡出" checked={config.erasePersist} onChange={(erasePersist) => onUpdate({ erasePersist })} />
              {!config.erasePersist && <RangeField label="恢复速度" value={config.autoRecover} min={1} max={100} unit="%" onChange={(autoRecover) => onUpdate({ autoRecover })} />}
            </>
          )}
          {config.mode === 'parallax' && (
            <>
              <RangeField label="深度位移" value={config.parallaxDepth} min={4} max={60} unit="px" onChange={(parallaxDepth) => onUpdate({ parallaxDepth })} />
              <RangeField label="镜头推进" value={config.parallaxZoom} min={0} max={24} unit="%" onChange={(parallaxZoom) => onUpdate({ parallaxZoom })} />
              <RangeField label="景深模糊" value={config.parallaxBlur} min={0} max={8} unit="px" onChange={(parallaxBlur) => onUpdate({ parallaxBlur })} />
              <RangeField label="图层平衡" value={config.parallaxBalance} min={20} max={80} unit="%" onChange={(parallaxBalance) => onUpdate({ parallaxBalance })} />
              <RangeField label="融合宽度" value={config.parallaxBlend} min={0} max={40} unit="%" onChange={(parallaxBlend) => onUpdate({ parallaxBlend })} />
              <RangeField label="跟随速度" value={config.follow} min={2} max={30} onChange={(follow) => onUpdate({ follow })} />
            </>
          )}
        </InspectorSection>

        <div className="interaction-note">
          <CircleDot size={15} />
          <p><strong>动作就是内容的一部分</strong><span>{active.metaphor}。当前效果会原样进入导出的独立网页。</span></p>
        </div>
      </div>
    </aside>
  );
}
